'use client';

import React from 'react';
import { Avatar } from '@heroui/avatar';
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
} from '@heroui/dropdown';
import { User } from '@heroui/user';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Icon from '@/components/Common/Icons/Icon';
import useAuth from '@/hooks/useAuth';
import { useTranslations } from 'next-intl';
import { useAppDispatch } from '@/hooks/redux';
import { showModal } from '@/store/slices/modalSlice';
import { setTypeForUserAccessLevel } from '@/store/slices/userAccessSlice';
import { usePlan } from '@/features/subscription/hooks/usePlan';
import { APP_EVENTS, logHubxEvent } from '@/app/providers/analytics';
import usePaddlePricing from '@/features/subscription/hooks/usePaddlePricing';
import { useCheckoutIntentPrefetch } from '@/features/subscription/hooks/useCheckoutIntentPrefetch';
import { resolvePaywallPriceId } from '@/features/subscription/utils/pricing/resolvePaywallPrice';

const AccountDropdown = () => {
  const { logout, user } = useAuth();
  const t = useTranslations('navbar');
  const dispatch = useAppDispatch();
  const { planIsActive } = usePlan();
  const { monthlyPrices, weeklyPrices, yearlyPrices } = usePaddlePricing();
  const { intentHandlers, triggerPrefetch } = useCheckoutIntentPrefetch({
    getPriceParams: () => {
      const priceId = resolvePaywallPriceId({
        selectedPlan: 'weekly',
        monthlyPrices,
        weeklyPrices,
        yearlyPrices,
        user,
      });

      return priceId
        ? { priceId, paywallType: 'account_dropdown_get_access' }
        : undefined;
    },
    enabled: !planIsActive,
  });

  React.useEffect(() => {
    triggerPrefetch();
  }, [triggerPrefetch]);

  const navigateWithFullReload = React.useCallback((href: string) => {
    if (typeof window === 'undefined') {
      return;
    }

    window.location.assign(href);
  }, []);

  const logoutHandler = () => {
    logout();
  };

  const openPaywall = () => {
    logHubxEvent({
      eventName: APP_EVENTS.CONVERSION_TRIGGER,
      event_params: { source: { value: 'account_dropdown_get_access' } },
    });
    dispatch(setTypeForUserAccessLevel('account_dropdown_get_access'));
    dispatch(
      showModal({
        modalKey: 'PaddlePayment',
        modalData: { isOnlyPayment: true, selectedPlan: 'weekly' },
      })
    );
  };

  return (
    <Dropdown className="!rounded-extra !min-w-[234px] max-w-[300px]">
      <DropdownTrigger>
        <Avatar
          src=""
          name={user?.name !== 'User' ? user?.name : user?.email}
          className="w-[38px] h-[38px] cursor-pointer"
          color={'primary'}
          classNames={{
            name: 'text-lg uppercase',
          }}
          getInitials={(name) =>
            name
              .split(' ')
              .map((n) => n[0])
              .join('')
              .slice(0, 2)
          }
        />
      </DropdownTrigger>
      <DropdownMenu>
        <DropdownItem
          key={'user'}
          onClick={() => navigateWithFullReload('/profile')}
          className="rounded-[3px] !p-[12px] hover:!bg-primary-extra-light-blue2 !text-gray-dark !text-[15px]"
        >
          <User
            name={user?.name !== 'User' ? user?.name : undefined}
            description={user?.email}
            avatarProps={{
              name: user?.name !== 'User' ? user?.name : user?.email,
              color: 'primary',
              className: 'w-[42px] md:w-12 h-[42px] md:h-12',
              getInitials: (name) =>
                name
                  .split(' ')
                  .map((n) => n[0])
                  .join('')
                  .slice(0, 2),
              classNames: {
                name: 'text-lg uppercase',
              },
            }}
            classNames={{
              name: '!text-gray-dark text-[17px] font-semibold truncate max-w-[180px]',
              description:
                user?.name !== 'User'
                  ? '!text-gray-light text-[13px] font-normal truncate max-w-[180px]'
                  : '!text-gray-dark text-[15px] font-normal truncate max-w-[180px]',
              wrapper: 'max-w-[200px]',
            }}
          />
        </DropdownItem>
        <DropdownItem
          key={'get-access'}
          className={
            planIsActive
              ? 'hidden'
              : 'rounded-[5px] !p-[12px] !bg-primary-extra-light-blue2 hover:!bg-primary-extra-light-blue2 !text-primary-blue'
          }
          classNames={{ title: 'text-[15px] font-semibold leading-[normal]' }}
          startContent={<Icon name="RocketLaunch" width={24} height={24} />}
          onClick={openPaywall}
          {...intentHandlers}
        >
          {t('get_access')}
        </DropdownItem>
        <DropdownItem
          key={'account'}
          className="rounded-[3px] !p-[12px] hover:!bg-primary-extra-light-blue2 !text-gray-dark !text-[15px]"
          startContent={<Icon name="AccountUser" width={14} height={16} />}
          onClick={() => navigateWithFullReload('/profile')}
        >
          {t('account')}
        </DropdownItem>
        <DropdownItem
          key={'settings'}
          className="rounded-[3px] !p-[12px] hover:!bg-primary-extra-light-blue2 !text-gray-dark !text-[15px]"
          startContent={
            <FontAwesomeIcon
              icon={['far', 'circle-question']}
              className="w-4 h-4 text-primary-blue"
            />
          }
          onClick={() => navigateWithFullReload('/faq')}
        >
          {t('help')}
        </DropdownItem>
        <DropdownItem
          key={'logout'}
          onClick={logoutHandler}
          className="rounded-[3px] !p-[12px] hover:!bg-primary-extra-light-blue2 !text-gray-dark !text-[15px]"
          startContent={<Icon name="SignOutAlt" width={16} height={16} />}
        >
          {t('logout')}
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
};

export default AccountDropdown;
